// react/libs
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import dayjs from 'dayjs';

// constants
import { CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID } from './CalendarInteractionProvider.consts';

// types
import { CalendarInteractionContext } from './CalendarInteractionProvider.context';
import {
  ExportCalculatedAppointmentOccurrencesBySeriesRequest,
  PlanningEntityIdentifier
} from 'generated/index';
import { CalendarAppointmentEvent } from 'components/mrp/components/Calendar/Calendar.types';

// utils
import { pasteEvent as pasteEventUtil } from './utils/pasteEvent';
import { duplicateEvent as duplicateEventUtil } from './utils/duplicateEvent';
import { createAppointment } from './utils/createAppointment';
import { convertFormFieldDataToPayload } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/SmartActionDialog.utils';
import { mutations } from 'RestAPIs/rapini';
import {
  showSuccessSnackbar,
  showErrorSnackbar
} from 'components/shared/Snackbar';

// stores/hooks/context
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { useShallow } from 'zustand/shallow';
import { useCalendarInteractionStore } from '../store/calendarInteractionStore.store';
import { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import { appointmentFormFieldEntitiesToPlanningEntityIdentifiers } from 'components/mrp/components/AppointmentForm/AppointmentForm.utils';
import { getPlanningEntityIdentifiersFromEvent } from '../../../Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';

export const CalendarInteractionProvider = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  const { sourceEvent, isCut, isProcessing, setSourceEvent, setIsProcessing } =
    useCalendarInteractionStore(
      useShallow((state) => ({
        sourceEvent: state.sourceEvent,
        isCut: state.isCut,
        isProcessing: state.isProcessing,
        setSourceEvent: state.control.setSourceEvent,
        setIsProcessing: state.control.setIsProcessing
      }))
    );

  const { calendars, timeStep } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      timeStep: state.timeStep
    }))
  );

  const { openDialog, onClose } = useContext(DialogContext);

  const [fetchEntities, setFetchEntities] = useState<
    PlanningEntityIdentifier[]
  >([]);

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: []
  });

  useEffect(() => {
    onClose?.(
      CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
      DialogType.SMART_ACTION_DIALOG,
      async (props) => {
        try {
          if (props?.ok) {
            await refetchByPlanningEntityIdentifiers(fetchEntities);
          }
        } catch (error) {
          console.error('Failed to refetch planning entities', error);
        } finally {
          if (isCut) {
            setSourceEvent(undefined, false);
          }

          setIsProcessing(false);
        }
      }
    );
  }, [
    fetchEntities,
    isCut,
    onClose,
    refetchByPlanningEntityIdentifiers,
    setIsProcessing,
    setSourceEvent
  ]);

  const { appointment, setAppointmentData } = useAppointmentStore(
    useShallow((state) => ({
      appointment: state.appointment,
      setAppointmentData: state.control.setAppointmentData
    }))
  );

  const { mutateAsync: createAppointmentMutation } =
    mutations.useCreateAppointment();

  const { mutateAsync: exportAppointmentSeriesMutation } =
    mutations.useExportCalculatedAppointmentOccurrencesBySeries();

  const createAppointmentDirectly = useCallback(
    async (
      formData: AppointmentFormFieldData,
      entitiesToRefetch: PlanningEntityIdentifier[]
    ) => {
      // For duplicates, suppress warnings (overlaps are expected)
      const payload = convertFormFieldDataToPayload(formData, true);

      return createAppointmentMutation(payload, {
        onSuccess: async () => {
          showSuccessSnackbar();
          // Refresh the calendar with the entities that need updating
          await refetchByPlanningEntityIdentifiers(entitiesToRefetch);
        },
        onError: () => {
          showErrorSnackbar();
        }
      });
    },
    [refetchByPlanningEntityIdentifiers, createAppointmentMutation]
  );

  const exportAppointmentSeriesDirectly = useCallback(
    async (
      event: CalendarAppointmentEvent,
      entitiesToRefetch: PlanningEntityIdentifier[]
    ) => {
      if (!event || isProcessing) return;

      const date = dayjs(event.planningEvent.start);
      const startDate = date.startOf('day');
      const endDate = startDate.add(1, 'year');

      const payload = {
        originalAppointmentId: event.planningEvent?.originalAppointmentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      } as ExportCalculatedAppointmentOccurrencesBySeriesRequest;

      return exportAppointmentSeriesMutation(payload, {
        onSuccess: async () => {
          showSuccessSnackbar();
          // Refresh the calendar with the entities that need updating
          await refetchByPlanningEntityIdentifiers(entitiesToRefetch);
        },
        onError: () => {
          showErrorSnackbar();
        }
      });
    },
    [
      isProcessing,
      exportAppointmentSeriesMutation,
      refetchByPlanningEntityIdentifiers
    ]
  );

  const pasteEvent = useCallback(
    (calendarId: string, date: dayjs.Dayjs) => {
      if (!sourceEvent || !calendarId || !date || isProcessing) return;

      pasteEventUtil({
        sourceEvent,
        calendars,
        isCut,
        calendarId,
        date,
        setIsProcessing,
        setFetchEntities,
        openDialog
      });
    },
    [sourceEvent, isProcessing, calendars, isCut, setIsProcessing, openDialog]
  );

  const addAppointment = useCallback(
    (calendarId?: string, start?: dayjs.Dayjs, durationInMinutes?: number) => {
      if (!!appointment) return;

      const calendar = calendars[calendarId || '-1'];
      const actualDuration = durationInMinutes || timeStep;

      const formData = createAppointment({
        start,
        calendar,
        durationInMinutes: actualDuration
      });

      setAppointmentData(formData);
    },
    [appointment, calendars, setAppointmentData, timeStep]
  );

  const updateAppointment = useCallback(
    (
      appointment?: AppointmentFormFieldData | null,
      oldEntities: PlanningEntityIdentifier[] = []
    ) => {
      if (!appointment) return;

      const entities = appointmentFormFieldEntitiesToPlanningEntityIdentifiers(
        Object.values(appointment.planEntities).flatMap((entity) => entity)
      ) satisfies PlanningEntityIdentifier[];

      setFetchEntities([...entities, ...oldEntities]);

      openDialog?.(
        CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
        DialogType.SMART_ACTION_DIALOG,
        {
          actionType: 'update',
          appointmentExceptions: appointment.appointmentExceptions,
          payload: appointment
        }
      );
    },
    [openDialog, setFetchEntities]
  );

  const deleteAppointment = useCallback(
    (appointment?: AppointmentFormFieldData | null) => {
      if (!appointment) return;

      const entities = appointmentFormFieldEntitiesToPlanningEntityIdentifiers(
        Object.values(appointment.planEntities).flatMap((entity) => entity)
      ) satisfies PlanningEntityIdentifier[];

      setFetchEntities(entities);

      openDialog?.(
        CALENDAR_INTERACTION_SMART_ACTION_DIALOG_ID,
        DialogType.SMART_ACTION_DIALOG,
        {
          actionType: 'delete',
          appointmentExceptions: appointment.appointmentExceptions,
          payload: appointment
        }
      );
    },
    [openDialog, setFetchEntities]
  );

  const exportAppointmentSeries = useCallback(
    async (event: CalendarAppointmentEvent) => {
      if (!event || isProcessing) return;

      // Entities from the appointment
      const sourceEntities = getPlanningEntityIdentifiersFromEvent(
        event.planningEvent
      );
      await exportAppointmentSeriesDirectly(event, sourceEntities);
    },
    [isProcessing, exportAppointmentSeriesDirectly]
  );

  const duplicateEvent = useCallback(
    async (event: CalendarAppointmentEvent) => {
      if (!event || isProcessing) return;

      await duplicateEventUtil({
        event,
        setIsProcessing,
        createAppointment: createAppointmentDirectly,
        asRecurring: false
      });
    },
    [isProcessing, setIsProcessing, createAppointmentDirectly]
  );

  const duplicateRecurringEvent = useCallback(
    async (event: CalendarAppointmentEvent) => {
      if (!event || isProcessing) return;

      await duplicateEventUtil({
        event,
        setIsProcessing,
        createAppointment: createAppointmentDirectly,
        asRecurring: true
      });
    },
    [isProcessing, setIsProcessing, createAppointmentDirectly]
  );

  const contextValue = useMemo(
    () => ({
      pasteEvent,
      addAppointment,
      deleteAppointment,
      updateAppointment,
      duplicateEvent,
      duplicateRecurringEvent,
      exportAppointmentSeries
    }),
    [
      pasteEvent,
      addAppointment,
      deleteAppointment,
      updateAppointment,
      duplicateEvent,
      duplicateRecurringEvent,
      exportAppointmentSeries
    ]
  );

  return (
    <CalendarInteractionContext.Provider value={contextValue}>
      {children}
    </CalendarInteractionContext.Provider>
  );
};
